import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { AddToBasketDocument } from '@packages/gql/generated/shopping/AddToBasketDocument';
import type {
  AddBasketServiceLineitem,
  AddToBasketMutation,
  AddToBasketMutationVariables,
} from '@packages/gql/generated/shopping/graphql';
import { getBridge } from '@packages/shared/src/utils/getBridge';

/* GraphQL */ `
  mutation AddToBasket(
    $locale: String!
    $sku: String!
    $count: Int!
    $isSpecialOrder: Boolean!
    $serviceLineitems: [AddBasketServiceLineitem!]
    $trackingParameter: BasketProductTrackingParameter
    $returnAllProducts: Boolean!
  ) {
    addToBasket(
      locale: $locale
      product: {
        sku: $sku
        count: $count
        isSpecialOrder: $isSpecialOrder
        basketOption: []
        serviceLineitems: $serviceLineitems
        trackingParameter: $trackingParameter
      }
      returnAllProducts: $returnAllProducts
    ) {
      itemCount
      success
      messages {
        ...MessageFragment
      }
      lineItems {
        product {
          sku
        }
        ...LineItemFragment
        ...TrackGlycerinAddToCartLineItem
      }
    }
  }
`;

type AddProductToBasketProps = {
  locale: string;
  sku: string;
  count: number;
  isSpecialOrder: boolean;
  serviceLineitems?: AddBasketServiceLineitem[];
  trackingParameter?: AddToBasketMutationVariables['trackingParameter'];
};
type AddProductToBasketResult =
  | {
      success: true;
      data: AddToBasketMutation['addToBasket'];
    }
  | {
      success: false;
      error?: Error;
      messages?: AddToBasketMutation['addToBasket']['messages'];
    };

export const useAddProductToBasket = () => {
  const [{ data, fetching }, executeAddToBasket] = useAuthMutation(AddToBasketDocument);

  const addProductToBasket = async (
    variables: AddProductToBasketProps,
  ): Promise<AddProductToBasketResult> => {
    const result = await executeAddToBasket({ ...variables, returnAllProducts: true });

    if (result.error) {
      return {
        success: false,
        error: result.error,
      };
    }
    if (result.data?.addToBasket && !result.data.addToBasket.success) {
      if (result.data.addToBasket.messages) {
        return {
          success: false,
          error: new Error('Failed to add product to basket (success false) with messages'),
          messages: result.data.addToBasket.messages,
        };
      }
      return {
        success: false,
        error: new Error('Failed to add product to basket (success false)'),
      };
    }
    if (result.data?.addToBasket) {
      getBridge().updateBasket();
      return {
        success: true,
        data: result.data.addToBasket,
      };
    }
    return {
      success: false,
      error: new Error('Unknown error occurred while adding product to basket'),
    };
  };

  return { data, fetching, addProductToBasket };
};
